'use client';

import React, { useState, useEffect, useRef } from 'react';
import cn from 'classnames';
import {
  curveSvg,
} from './TransitionSvg3D';
import styles from './Transition3D.module.scss';

export type Transition3DPhase = 'initial' | 'revealing' | 'complete' | 'transferred' | 'agent' | 'ended';

export interface TransitionAnimation3DProps {
  /** Whether the transition is active */
  isActive?: boolean;
  /** Additional CSS class */
  className?: string;
  /** Children to render (revealed by the wave) */
  children?: React.ReactNode;
  /** Whether the transition is complete */
  isComplete?: boolean;
  /** Controlled phase: 'initial' | 'revealing' | 'complete'. If provided, component becomes controlled */
  phase?: Transition3DPhase;
  /** Whether the last participant is an agent */
  isLastParticipantAgent?: boolean;
}

export const TransitionAnimation3D: React.FC<TransitionAnimation3DProps> = ({
  isActive = true,
  className,
  children,
  isComplete = false,
  phase: controlledPhase,
  isLastParticipantAgent = false,
}) => {
  const [internalPhase, setInternalPhase] = useState<Transition3DPhase>('initial');
  const [initialAnimDone, setInitialAnimDone] = useState(false);
  const waveContainerRef = useRef<HTMLDivElement>(null);

  // Use controlled phase if provided, otherwise use internal state
  const phase = controlledPhase !== undefined ? controlledPhase : internalPhase;
  const isControlled = controlledPhase !== undefined;

  // When phase is 'revealing', isComplete should also be true
  const effectiveIsComplete = phase === 'revealing' ? true : isComplete;

  useEffect(() => {
    // Only manage internal phase if component is uncontrolled
    if (isControlled) return;

    if (effectiveIsComplete) {
      setInternalPhase('complete');
      return;
    }
  }, [effectiveIsComplete, isControlled]);

  useEffect(() => {
    // Only manage internal phase if component is uncontrolled
    if (isControlled) return;

    // Only reset if explicitly set to inactive AND not already complete
    if (!isActive && internalPhase !== 'complete') {
      setInternalPhase('initial');
      return;
    }

    // Don't re-run animation if already complete
    if (internalPhase === 'complete' || effectiveIsComplete) return;

    if (!isActive) return;

    document.body.style.overflow = 'hidden';
  }, [isActive, internalPhase, isComplete, isControlled, effectiveIsComplete]);

  // Once the initial wave animation finishes, lock the final state so
  // re-renders can never restart it.
  useEffect(() => {
    if (phase !== 'initial' || initialAnimDone) return;
    const container = waveContainerRef.current;
    if (!container) return;

    const handleAnimEnd = (e: AnimationEvent) => {
      if (e.animationName && e.animationName.includes('wave3DInitial')) {
        setInitialAnimDone(true);
      }
    };

    container.addEventListener('animationend', handleAnimEnd as EventListener);
    return () => container.removeEventListener('animationend', handleAnimEnd as EventListener);
  }, [phase, initialAnimDone]);

  const isEndedAgentVisualState = phase === 'ended' && isLastParticipantAgent;

  // Build class names based on phase and isComplete
  const containerClasses = cn(
    styles.container3d,
    className,
    {
      [styles.initial]: phase === 'initial' && !initialAnimDone,
      [styles.initialDone]: phase === 'initial' && initialAnimDone,
      [styles.revealing]: phase === 'revealing',
      [styles.complete]: phase === 'complete',
      [styles.transferred]: phase === 'transferred',
      [styles.agent]: phase === 'agent',
      [styles.ended]: isEndedAgentVisualState,
    }
  );


  // Only show SVG curves when in Welcome Buttons screen
  const showCurves = phase === 'initial';

  return (<div className={containerClasses}> {/* Top blue fill used only in initial phase to replace cream */}
    {showCurves && (
      <>
        <div className={styles.topBlueFill} aria-hidden="true" />

        <div className={styles.waveContainer3d} ref={waveContainerRef}>
          <div className={styles.rotatingWave} dangerouslySetInnerHTML={{ __html: curveSvg }} />
        </div>
      </>
    )}

    {/* Content layer */}
    {children}
  </div>
  );
};

export default TransitionAnimation3D;
